import { useApiClient } from '~/api-client';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { useCallback, useState } from 'react';
import { DexResultsStore } from './dex-results-store';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { useBootstrapState } from '../../bootstrap/bootstrap-provider';
import { useIntegratedStreams } from '../../common/hooks/use-integrated-streams';
import { useDexResultsBroadcastListener } from './use-dex-results-broadcast-listener';
import { DexResultsResource, DexResultsView } from '@mark43/dex-api';
import { useApplicationType } from '../../common/hooks/use-application-type';
import { useAudibleAlerts } from '../../sounds/play-sound-provider';
import { useSelectDexResults } from './use-select-dex-results';

// Called with the DEX results each dex_nudge brings back
export type DexResultsListener = (dexResultsViews: DexResultsView[]) => void;

export type IntegratedDexResultsReturn = {
    dexResultsStore: DexResultsStore;
    getDexResultsQuery: UseQueryResult<DexResultsView[] | undefined, unknown>;
    dexResultsListeners: Set<DexResultsListener>;
};

export function useIntegratedDexResults(): IntegratedDexResultsReturn {
    const { isCad, isFirstResponder } = useApplicationType();
    const apiClient = useApiClient();
    const {
        dataExchange,
        dexNextEnabled,
        playAlertOnHotHitResultForOfficerDexQuery,
        firstResponderPlayAlertOnHotHitDexResult,
    } = useEnabledFeatures();
    const { userProfileView, latestSeenDexIncomingMessageId } = useBootstrapState();
    const [dexResultsStore] = useState<DexResultsStore>(
        () => new DexResultsStore(userProfileView.userId, latestSeenDexIncomingMessageId)
    );
    const [dexResultsListeners] = useState(() => new Set<DexResultsListener>());
    const { selectDexResultsSummary } = useSelectDexResults(dexResultsStore);
    const { playAlert } = useAudibleAlerts();
    const fetchDexResultsViews = useCallback(
        (outgoingMessageIds: number[]) => {
            return apiClient
                .request<DexResultsResource.GetDexResultsViewForOutgoingMessageIds>({
                    service: 'DEX',
                    method: 'POST',
                    url: 'dex_results',
                    data: {
                        outgoingMessageIds,
                    },
                })
                .then((response) => response.data.data)
                .then((dexResultsViews) => {
                    if (dexResultsViews) {
                        dexResultsListeners.forEach((listener) => listener(dexResultsViews));
                        const {
                            hasNewHotHitFromOtherUserQuery,
                            hasNewHotHitFromOwnQuery,
                            outgoingMessageIdsWithNewSummaryViewFromOwnQuery,
                        } = dexResultsStore.upsertDexResultsViews(
                            dexResultsViews,
                            isFirstResponder
                        );
                        playAlert({
                            type: 'DEX_HOT_HIT',
                            context: {
                                enabledFeatures: {
                                    playAlertOnHotHitResultForOfficerDexQuery,
                                    firstResponderPlayAlertOnHotHitDexResult,
                                },
                                isCad,
                            },
                            hasNewHotHitFromOtherUserQuery,
                            hasNewHotHitFromOwnQuery,
                        });
                        if (
                            !dexResultsStore.getSelectedResultId() &&
                            !dexResultsStore.getSelectedSummaryId() &&
                            outgoingMessageIdsWithNewSummaryViewFromOwnQuery.length > 0
                        ) {
                            const newestOutgoingMessageId = Math.max(
                                ...outgoingMessageIdsWithNewSummaryViewFromOwnQuery
                            );
                            selectDexResultsSummary(newestOutgoingMessageId);
                        }
                    }
                });
        },
        [
            apiClient,
            dexResultsStore,
            dexResultsListeners,
            playAlertOnHotHitResultForOfficerDexQuery,
            firstResponderPlayAlertOnHotHitDexResult,
            isCad,
            isFirstResponder,
            playAlert,
            selectDexResultsSummary,
        ]
    );

    const getDexResultsQuery = useQuery(
        ['dex-results'],
        () => {
            return apiClient
                .request<DexResultsResource.GetDexResultsViewsForActingUserInbox>({
                    service: 'DEX',
                    method: 'GET',
                    url: 'dex_results/user',
                })
                .then((response) => response.data.data);
        },
        {
            onSuccess(dexResultsViews) {
                dexResultsStore.setDexResultsViews(dexResultsViews, isFirstResponder);
            },
            enabled: false,
        }
    );

    useDexResultsBroadcastListener(dexResultsStore, getDexResultsQuery);

    useIntegratedStreams({
        enabled: dataExchange && !dexNextEnabled,
        streamNames: ['dex_nudge'],
        query: getDexResultsQuery,
        onMessage: (message) => [message.payload],
        promiseBufferCallback: (inputs) => {
            return fetchDexResultsViews(inputs);
        },
    });

    return {
        dexResultsStore,
        getDexResultsQuery,
        dexResultsListeners,
    };
}
